import React, { useEffect, useId, useRef, useState } from 'react';
import { BOOK_LANGUAGE_NAMES_EN } from '@living-library/contracts';
import { button, MetadataPreview } from './editors';
import type { Metadata } from './model';

export interface ProviderCandidate extends Metadata {
  source: 'nkp_skc' | 'open_library' | 'isbndb';
  sourceId: string;
  sourceUrl: string | null;
  coverImageUrl: string | null;
}
const providerNames = { nkp_skc: 'NKP catalogue', open_library: 'Open Library', isbndb: 'ISBNdb' };
function CandidateCover({ book, horizontal }: { book: ProviderCandidate; horizontal: boolean }) {
  const [failed, setFailed] = useState(false);
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-lg bg-canvas ${horizontal ? 'h-36 w-24 sm:h-44 sm:w-28' : 'h-40 w-full sm:h-44'}`}
    >
      {book.coverImageUrl && !failed ? (
        <img
          src={book.coverImageUrl}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          className="h-full max-w-full rounded object-contain shadow-sm"
        />
      ) : (
        <span className="flex flex-col items-center gap-2 px-2 text-center text-ink-muted">
          <svg aria-hidden="true" width="28" height="34" viewBox="0 0 28 34" fill="none">
            <path
              d="M5 2h20v30H5a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3Z"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <path d="M7 2v25m-2 0h20M11 9h9m-9 5h6" stroke="currentColor" strokeWidth="1.4" />
          </svg>
          <span className="text-xs">No cover</span>
        </span>
      )}
    </span>
  );
}
export function ProviderResults({
  items,
  prepare,
  preparing = false,
}: {
  items: ProviderCandidate[];
  prepare(book: ProviderCandidate): void;
  preparing?: boolean;
}) {
  const [opened, setOpened] = useState<ProviderCandidate | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const details = useRef<HTMLElement>(null),
    detailsId = useId();
  useEffect(() => {
    if (opened) details.current?.focus();
  }, [opened]);
  const horizontal = items.length === 1;
  return (
    <div className="provider-results">
      <div className={horizontal ? 'provider-results-single' : 'provider-results-grid'}>
        {items.map((book) => {
          const selected = opened?.sourceId === book.sourceId && opened?.isbn === book.isbn;
          return (
            <button
              type="button"
              key={`${book.source}:${book.sourceId}:${book.isbn}`}
              aria-label={`View details for ${book.title} (${book.sourceId})`}
              aria-expanded={selected}
              aria-controls={detailsId}
              onClick={(event) => {
                trigger.current = event.currentTarget;
                setOpened(book);
              }}
              className={`group flex min-w-0 w-full rounded-xl border p-4 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-thread ${horizontal ? 'flex-row items-center gap-5 sm:p-5' : 'h-full flex-col gap-4'} ${selected ? 'border-thread bg-thread-soft' : 'border-line/65 bg-panel-raised hover:border-thread/60 hover:bg-panel'}`}
            >
              <CandidateCover key={book.coverImageUrl} book={book} horizontal={horizontal} />
              <span className="flex min-w-0 flex-1 flex-col items-start gap-2 self-stretch">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                  {providerNames[book.source]} · {book.language.toUpperCase()}
                </span>
                <span
                  className={`line-clamp-3 break-words font-serif leading-snug text-ink ${horizontal ? 'text-2xl sm:text-3xl' : 'text-xl'}`}
                >
                  {book.title}
                </span>
                <span className="line-clamp-2 text-sm text-ink-soft">
                  {book.authors.join(', ') || 'Author unknown'}
                </span>
                <span className="line-clamp-2 text-xs leading-5 text-ink-muted">
                  {[book.publisher, book.publishedYear].filter(Boolean).join(' · ') ||
                    'Publication details unavailable'}
                </span>
                {horizontal && (
                  <span className="break-all text-xs text-ink-muted">
                    {book.isbn ? `ISBN ${book.isbn}` : 'No ISBN supplied'}
                  </span>
                )}
                <span className="mt-auto flex items-center gap-2 pt-3 text-xs font-semibold text-thread">
                  View edition details <span aria-hidden="true">↗</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
      {opened && (
        <section
          id={detailsId}
          ref={details}
          tabIndex={-1}
          aria-label="Provider edition details"
          className="mt-4 rounded-xl border border-thread/50 bg-panel-raised p-5 outline-thread"
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h5 className="font-serif text-xl">Edition details</h5>
              <p className="mt-1 text-xs text-ink-muted">
                {providerNames[opened.source]} · {opened.sourceId} ·{' '}
                {BOOK_LANGUAGE_NAMES_EN[opened.language] ?? opened.language}
              </p>
            </div>
            <button
              type="button"
              className={button}
              onClick={() => {
                setOpened(null);
                trigger.current?.focus();
              }}
            >
              Close details
            </button>
          </div>
          <MetadataPreview value={opened} />
          {(!opened.binding || !opened.isbn) && (
            <p className="mb-3 text-xs text-ink-muted">
              {[
                !opened.binding && 'Binding not supplied by provider',
                !opened.isbn && 'No ISBN supplied',
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
          <p className="mb-4 text-sm leading-6 text-ink-muted">
            Loading fetches the exact edition and saves its public provider metadata. Review the
            loaded edition before proposing a match. Your reader’s private book stays unchanged.
          </p>
          <button type="button" className="admin-button-primary" onClick={() => prepare(opened)}>
            {preparing ? 'Loading edition…' : 'Load edition into catalogue'}
          </button>
        </section>
      )}
    </div>
  );
}
